import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiCheck, FiChevronDown, FiTag, FiX } from "react-icons/fi";
import type { Taxonomie } from "../../API";

interface TaxonomyFilterProps {
	taxonomies: Taxonomie[];
	value: string[];
	onChange: (ids: string[]) => void;
}

/**
 * Filtre taxonomies compact pour la barre d'outils de la Médiathèque : un bouton-pilule avec
 * compteur, un menu de cases à cocher (recherche locale au-delà de quelques entrées) et un
 * raccourci « Effacer ». Le choix remonte au parent qui le miroite dans l'URL (?tax=).
 */
export const TaxonomyFilter: React.FC<TaxonomyFilterProps> = ({ taxonomies, value, onChange }) => {
	const [open, setOpen] = useState(false);
	const [filter, setFilter] = useState("");
	const containerRef = useRef<HTMLDivElement | null>(null);

	const options = useMemo(
		() =>
			taxonomies
				.filter((taxonomy) => Boolean(taxonomy.id))
				.slice()
				.sort((a, b) => (a.title ?? a.id ?? "").localeCompare(b.title ?? b.id ?? "", "fr-FR", { sensitivity: "base" })),
		[taxonomies]
	);

	const filtered = useMemo(() => {
		const term = filter.trim().toLowerCase();
		if (!term) return options;
		return options.filter((option) => (option.title ?? option.id ?? "").toLowerCase().includes(term));
	}, [options, filter]);

	const selected = useMemo(() => new Set(value), [value]);

	useEffect(() => {
		if (!open) return;
		const onPointerDown = (event: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
				setOpen(false);
			}
		};
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") setOpen(false);
		};
		document.addEventListener("mousedown", onPointerDown);
		document.addEventListener("keydown", onKeyDown);
		return () => {
			document.removeEventListener("mousedown", onPointerDown);
			document.removeEventListener("keydown", onKeyDown);
		};
	}, [open]);

	useEffect(() => {
		if (!open) setFilter("");
	}, [open]);

	if (!options.length) return null;

	const toggle = (id: string) => {
		onChange(selected.has(id) ? value.filter((item) => item !== id) : [...value, id]);
	};

	const active = value.length > 0;

	return (
		<div ref={containerRef} className="relative">
			<button
				type="button"
				onClick={() => setOpen((current) => !current)}
				aria-expanded={open}
				aria-haspopup="true"
				title="Filtrer par taxonomies"
				className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold shadow-sm transition ${
					active
						? "border-[#005CA9] bg-[#005CA9]/10 text-[#005CA9]"
						: "border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-700"
				}`}
			>
				<FiTag className="h-3.5 w-3.5" aria-hidden />
				Taxonomies
				{active && (
					<span className="inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-[#005CA9] px-1 text-[10px] font-bold text-white">
						{value.length}
					</span>
				)}
				<FiChevronDown className={`h-3.5 w-3.5 transition ${open ? "rotate-180" : ""}`} aria-hidden />
			</button>

			{open && (
				<div className="absolute right-0 z-50 mt-2 w-72 rounded-xl border border-slate-200 bg-white p-2 shadow-lg">
					{options.length > 8 && (
						<input
							type="search"
							value={filter}
							onChange={(event) => setFilter(event.target.value)}
							placeholder="Filtrer la liste…"
							aria-label="Filtrer la liste des taxonomies"
							className="mb-2 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-700 placeholder:text-slate-400 focus:border-slate-400 focus:bg-white focus:outline-none"
						/>
					)}
					<ul className="max-h-64 space-y-0.5 overflow-auto" role="listbox" aria-multiselectable="true">
						{filtered.length === 0 ? (
							<li className="px-2 py-1.5 text-xs text-slate-400">Aucune taxonomie ne correspond.</li>
						) : (
							filtered.map((option) => {
								const id = option.id!;
								const isSelected = selected.has(id);
								return (
									<li key={id}>
										<button
											type="button"
											role="option"
											aria-selected={isSelected}
											onClick={() => toggle(id)}
											className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs font-medium transition ${
												isSelected ? "bg-[#005CA9]/10 text-[#005CA9]" : "text-slate-700 hover:bg-slate-50"
											}`}
										>
											<span
												className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
													isSelected ? "border-[#005CA9] bg-[#005CA9] text-white" : "border-slate-300 bg-white"
												}`}
											>
												{isSelected && <FiCheck className="h-3 w-3" aria-hidden />}
											</span>
											<span className="truncate">{option.title || id}</span>
										</button>
									</li>
								);
							})
						)}
					</ul>
					{active && (
						<button
							type="button"
							onClick={() => onChange([])}
							className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-slate-200 px-2 py-1.5 text-xs font-semibold text-slate-500 transition hover:bg-slate-50 hover:text-slate-700"
						>
							<FiX className="h-3.5 w-3.5" aria-hidden /> Effacer le filtre
						</button>
					)}
				</div>
			)}
		</div>
	);
};
